import DefaultLayout from "src/components/common/layouts/DefaultLayout"
import {
  URLQueryParams,
  useCreatorSearch,
} from "src/components/hooks/useCreatorSearch"
import { useToggleR18 } from "src/components/hooks/useToggleR18"
import { useLoadMore } from "src/components/hooks/useLoadMore"
import SearchResultHeader from "src/components/pages/creator/SearchResultHeader"
import SearchResultNovelItem from "src/components/SearchResultNovelItem"
import HTMLHead from "src/components/utils/HTMLHead"
import getSearchQueryParamsFromURL from "src/libs/getSearchQueryParamsFromURL"
import { GetServerSideProps } from "next"
import { useRouter } from "next/router"
import { useCallback, useEffect, useState } from "react"
import { usePrevious } from "react-use"
import { Novel } from "src/models/tieup/novel/Novel"
import NovelDetail from "src/components/pages/creator/novel/NovelDetail"

type Props = {
  queryParams: URLQueryParams
}

const LIMIT = 10

const CreatorNovels: React.FC<Props> = ({ queryParams }) => {
  const {
    updateOrder,
    updateTieup,
    updateOverCharLimit,
    urlParams,
    searchQueryString,
    updateUrlParams,
    createApiSearchQueryString,
  } = useCreatorSearch()

  const { showR18, toggleShowR18 } = useToggleR18()

  const router = useRouter()

  const prevQuery = usePrevious(searchQueryString)

  useEffect(() => {
    updateUrlParams(queryParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams])

  useEffect(() => {
    if (searchQueryString === "" && prevQuery) {
      router.replace(`${router.pathname}`)
    } else if (searchQueryString && searchQueryString !== prevQuery) {
      router.replace(`${router.pathname}?${searchQueryString}`)
    }
  }, [prevQuery, router, searchQueryString])

  const getKey = useCallback(
    (index: number) => {
      const query = createApiSearchQueryString(index, LIMIT, showR18)

      return `/v1/mypage/novel/search?${query}`
    },
    [createApiSearchQueryString, showR18],
  )

  const { hasLoaded, data, hasNext, intersectionRef, onLoadMore } =
    useLoadMore<Novel>(getKey)

  const [selectedNovel, setSelectedNovel] = useState<Novel | null>(null)

  const onOpenModal = useCallback((novel: Novel) => {
    setSelectedNovel(novel)
  }, [])

  const onCloseModal = useCallback(() => {
    setSelectedNovel(null)
  }, [])

  return (
    <DefaultLayout isCreatorMyPage={true}>
      <HTMLHead path="creator/search/novels" />
      <NovelDetail novel={selectedNovel} onClose={onCloseModal} />
      <main>
        <div className="l-single">
          <SearchResultHeader
            title="検索結果一覧"
            searchWord={queryParams.q || ""}
            setIsTieup={updateTieup}
            isTieup={urlParams.tieup || ""}
            showR18Toggle={true}
            showR18={showR18}
            handleChangeR18Toggle={toggleShowR18}
            showOverCharLimitToggle={true}
            isOverCharLimit={urlParams.eu || ""}
            setIsOverCharLimit={updateOverCharLimit}
            order={urlParams.o || ""}
            setOrder={updateOrder}
          />
          {hasLoaded && data.length <= 0 && (
            <p className="p-searchResult-nothing">
              検索結果が見つかりませんでした
            </p>
          )}
          <div className="p-searchNovalResult-list">
            {!!data &&
              data.map((novel: Novel) => (
                <SearchResultNovelItem
                  key={novel.id}
                  novel={novel}
                  onClick={() => onOpenModal(novel)}
                />
              ))}
          </div>
          <div className="p-button -spacer-sm">
            {data.length > 0 && hasNext && (
              <button
                className="c-button-secondary -outline -full"
                onClick={onLoadMore}
                ref={intersectionRef}
              >
                もっと見る
              </button>
            )}
          </div>
        </div>
      </main>
    </DefaultLayout>
  )
}

export default CreatorNovels

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  return {
    props: {
      queryParams: getSearchQueryParamsFromURL(query),
    },
  }
}
